import React, {FC, useState} from "react";
import {Alert, Button, Form, Input, Rate, Space, Spin, Table, Typography, App} from "antd";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    HotelIdentityCreateDtoInput,
    useHotelCatalogQuery,
    useCreateCatalogHotelMutation,
    useUpdateCatalogHotelMutation,
    useDeleteCatalogHotelMutation
} from "../../GraphQL/generated/graphql";
import "./HotelsPage.css";
import {DeletePopconfirm} from "../../Components/DeleteConfirmation";
import {AdminPage, PageHeader} from "../../Components/ui";
import {shellBreadcrumbKeys, useShellBreadcrumbLabel} from "../../Contexts/ShellBreadcrumbContext";

type Hotel = HotelIdentityCreateDtoInput & {id: number};
const emptyHotel: HotelIdentityCreateDtoInput = {name: "", category: "", address: "", city: "", country: "", notes: ""};

const hotelCategoryStars = (category?: string | null): number => {
    const value = Number(category);
    return Number.isInteger(value) && value >= 1 && value <= 5 ? value : 0;
};

type HotelCategoryRatingProps = {
    value?: string | null;
    onChange?: (value: string) => void;
    id?: string;
    readOnly?: boolean;
};

const HotelCategoryRating: FC<HotelCategoryRatingProps> = ({value, onChange, id, readOnly = false}) => {
    const {t} = useTranslation();
    const stars = hotelCategoryStars(value);

    if (readOnly && stars === 0) {
        return <Typography.Text type="secondary">-</Typography.Text>;
    }

    return (
        <span className={`hotel-category-rating ${readOnly ? "hotel-category-rating--display" : "hotel-category-rating--input"}`}>
            <Rate
                id={id}
                count={5}
                allowClear={!readOnly}
                disabled={readOnly}
                value={stars}
                onChange={nextValue => onChange?.(nextValue ? String(nextValue) : "")}
                aria-label={readOnly
                    ? t("hotels-category-stars", {count: stars, defaultValue: "Hotel category: {{count}} of 5 stars"})
                    : t("hotels-category-select", {defaultValue: "Select hotel category"})}
            />
            <Typography.Text type="secondary">
                {stars ? `${stars}/5` : t("hotels-category-not-selected", {defaultValue: "Not selected"})}
            </Typography.Text>
        </span>
    );
};

const HotelEditor: FC<{hotel?: Hotel}> = ({hotel}) => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [create, createState] = useCreateCatalogHotelMutation();
    const [update, updateState] = useUpdateCatalogHotelMutation();
    const [remove, removeState] = useDeleteCatalogHotelMutation();
    const [error, setError] = useState("");
    const busy = createState.loading || updateState.loading || removeState.loading;
    useShellBreadcrumbLabel(hotel ? shellBreadcrumbKeys.hotel(hotel.id) : undefined, hotel?.name);
    const save = async (values: HotelIdentityCreateDtoInput) => {
        setError("");
        const input = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value?.trim() ?? ""])) as HotelIdentityCreateDtoInput;
        try {
            if (hotel) {
                await update({variables: {input: {...input, id: hotel.id}}});
            } else {
                const result = await create({variables: {input}});
                const id = result.data?.createHotelIdentity[0]?.id;
                if (!id) throw new Error(t("hotels-save-failed"));
                navigate(`/Admin/Hotels/${id}`, {replace: true});
            }
            message.success(t("hotels-saved"));
        } catch (e) {
            setError(e instanceof Error ? e.message : t("hotels-save-failed"));
        }
    };
    const deleteHotel = async () => {
        if (!hotel) return;
        setError("");
        try {
            const result = await remove({variables: {id: hotel.id}});
            if (!result.data?.deleteHotelIdentity) throw new Error(t("hotels-delete-failed"));
            message.success(t("hotels-deleted"));
            navigate("/Admin/Hotels");
        } catch (e) {
            const text = e instanceof Error ? e.message : "";
            setError(text.includes("linked to an event") ? t("hotels-in-use") : text || t("hotels-delete-failed"));
        }
    };
    return <div className="ati-stack ati-stack--loose ati-stack--narrow">
        {hotel && <Alert type="info" message={t("hotels-shared-info")}/>}
        {error && <Alert type="error" message={error} showIcon/>}
        <Form layout="vertical" initialValues={hotel ?? emptyHotel} onFinish={save} disabled={busy}>
            <Form.Item
                name="name"
                label={t("hotels-field-name")}
                rules={[{required: true, whitespace: true, message: t("hotels-name-required")}]}
            >
                <Input/>
            </Form.Item>
            <Form.Item name="category" label={t("hotels-field-category")}>
                <HotelCategoryRating/>
            </Form.Item>
            {(["address", "city", "country", "notes"] as const).map(field =>
                <Form.Item key={field} name={field} label={t(`hotels-field-${field}`)}>
                    {field === "notes" ? <Input.TextArea rows={4}/> : <Input/>}
                </Form.Item>)}
            <Space>
                <Button type="primary" htmlType="submit" loading={createState.loading || updateState.loading}>{t("hotels-save")}</Button>
                {hotel && <DeletePopconfirm
                    itemType={t("delete-target-hotel", {defaultValue: "hotel"})}
                    itemName={hotel.name}
                    onConfirm={deleteHotel}
                >
                    <Button danger loading={removeState.loading}>{t("hotels-delete")}</Button>
                </DeletePopconfirm>}
            </Space>
        </Form>
    </div>;
};

const HotelsPage: FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {hotelId} = useParams();
    const isNew = hotelId === "new";
    const id = Number(hotelId);
    const invalidId = !!hotelId && !isNew && (!Number.isSafeInteger(id) || id <= 0);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const query = useHotelCatalogQuery({
        skip: isNew || invalidId,
        fetchPolicy: "cache-and-network",
        variables: {
            skip: hotelId ? 0 : (page - 1) * 20,
            take: hotelId ? 1 : 20,
            where: hotelId ? {id: {eq: id}} : search ? {or: [
                {name: {contains: search}}, {city: {contains: search}}, {country: {contains: search}}
            ]} : undefined
        }
    });
    const hotels = query.data?.hotelIdentityCatalog?.items ?? [];
    return <AdminPage>
     <PageHeader title={t("hotels")}/>
        {isNew ? <HotelEditor key="new"/> : invalidId ? <Alert type="error" message={t("hotels-not-found")}/> : query.error ?
            <Alert type="error" message={query.error.message} action={<Button onClick={() => query.refetch()}>{t("hotels-retry")}</Button>}/> : hotelId ?
                query.loading && !hotels.length ? <Spin/> : hotels[0] ? <HotelEditor key={hotels[0].id} hotel={hotels[0]}/> :
                    <Alert type="warning" message={t("hotels-not-found")}/> :
                <div className="ati-stack ati-stack--loose">
                    <Space wrap>
                        <Input.Search aria-label={t("hotels-search")} placeholder={t("hotels-search")} allowClear
                                      onSearch={value => {setSearch(value.trim()); setPage(1);}} style={{width: 320, maxWidth: "100%"}}/>
                        <Button type="primary" onClick={() => navigate("/Admin/Hotels/new")}>{t("hotels-new")}</Button>
                    </Space>
                    <Table rowKey="id" loading={query.loading} dataSource={hotels} scroll={{x: 650}}
                           pagination={{current: page, pageSize: 20, total: query.data?.hotelIdentityCatalog?.totalCount ?? 0,
                               showSizeChanger: false, onChange: setPage}}
                           columns={[
                               {title: t("hotels-field-name"), dataIndex: "name", render: (name, hotel) =>
                                   <a href={`/Admin/Hotels/${hotel.id}`} onClick={event => {event.preventDefault(); navigate(`/Admin/Hotels/${hotel.id}`);}}>{name}</a>},
                               {title: t("hotels-field-category"), dataIndex: "category", width: 180,
                                   render: category => <HotelCategoryRating value={category} readOnly/>},
                               ...(["city", "country", "address"] as const).map(field => ({title: t(`hotels-field-${field}`), dataIndex: field}))
                           ]}/>
                </div>}
    </AdminPage>;
};

export default HotelsPage;
